import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink, Printer, Eye, MessageCircle, MousePointerClick } from "lucide-react";
import { api, extractPublicUrl } from "../../lib/api";
import { ExtractForm } from "./ExtractForm";


const EMPTY_EXTRACT = {
  title: "", author: "", slug: "", cover_url: "", book_id: "", publisher: "", genre: "", content: "", reading_time: "2-3 min",
  price_label: "", paper_available: true, price_paper_label: "", price_digital_label: "", whatsapp_number: "+33780951217", whatsapp_message: "Bonjour, je viens de lire l\'histoire [Titre] sur le Distributeur d\'extraits littéraires CLED et je souhaite commander le livre ([Version]).",
  secondary_cta_label: "Voir la fiche du livre", secondary_cta_url: "", partner_name: "", partner_logo_url: "", published: true, featured: false,
};
const opts = { withCredentials: true };

export function ExtractsPanel() {
  const [items, setItems] = useState([]);
  const [books, setBooks] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/extracts", opts).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); api.get("/books").then((r) => setBooks(r.data)).catch(() => {}); }, []);

  const save = async () => {
    if (!editing.title?.trim() || !editing.author?.trim() || !editing.content?.trim()) {
      toast.error("Le titre, l'auteur et le texte de l'extrait sont obligatoires");
      return;
    }
    const payload = Object.fromEntries(Object.entries(editing).map(([k, v]) => [k, v === "" ? null : v]));
    payload.reading_time = payload.reading_time || "2-3 min";
    if (payload.price_paper_label || payload.price_digital_label) payload.price_label = null;
    try {
      if (editing.id) await api.put(`/admin/extracts/${editing.id}`, payload, opts);
      else await api.post("/admin/extracts", payload, opts);
      toast.success("Extrait enregistré");
      setEditing(null); load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Vérifiez les champs obligatoires (titre, auteur, texte)");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cet extrait ? Les QR codes déjà imprimés ne fonctionneront plus.")) return;
    try {
      await api.delete(`/admin/extracts/${id}`, opts);
      toast.success("Extrait supprimé");
      load();
    } catch {
      toast.error("La suppression a échoué");
    }
  };

  return (
    <div data-testid="extracts-panel">
      <div className="flex items-center justify-between mb-4 gap-4">
        <h2 className="font-serif text-4xl text-[#1A1A1A] font-light">Extraits QR ({items.length})</h2>
        <div className="flex gap-2">
        <button onClick={() => setEditing({ ...EMPTY_EXTRACT })} data-testid="new-extract-button" className="bg-[#2C402E] text-[#FAF9F6] px-4 py-2 text-sm font-body flex items-center gap-2 whitespace-nowrap"><Plus size={14} /> Nouvel extrait</button>
        </div>
      </div>
      <p className="text-sm font-body text-[#757575] mb-8 max-w-3xl">
        Chaque extrait possède sa page de lecture mobile (<code>/histoires/…</code>) et sa propre affiche QR. Pour un lieu partenaire (banque, salle d'attente, librairie…), utilisez plutôt l'onglet « Partenaires » : un seul QR code donne accès à toutes les histoires qui lui sont attribuées.
      </p>
      {editing && <ExtractForm value={editing} books={books} onChange={setEditing} onSave={save} onCancel={() => setEditing(null)} />}
      <ul className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-8">
        {items.map((x) => <ExtractCard key={x.id} item={x} onEdit={() => setEditing({ ...EMPTY_EXTRACT, ...x })} onDelete={() => remove(x.id)} />)}
      </ul>
    </div>
  );
}

const Stat = ({ icon: Icon, value, label, plural, testid }) => {
  const text = value > 1 ? plural : label;
  return <span data-testid={testid} title={text} className="flex items-center gap-1"><Icon size={12} className="text-[#9E4735]" />{value} <span className="hidden sm:inline">{text}</span></span>;
};

const ExtractCard = ({ item, onEdit, onDelete }) => {
  return (
  <li data-testid={`admin-extract-${item.id}`} className="flex gap-4 border border-[#D1CFC7] p-4">
    <div className="bg-white p-1.5 border border-[#D1CFC7] self-start flex-shrink-0"><QRCodeSVG value={extractPublicUrl(item.slug)} size={72} /></div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <p className="font-serif text-lg text-[#1A1A1A] truncate">{item.title}</p>
        {item.featured && <span className="text-[10px] uppercase tracking-[0.2em] font-body px-2 py-0.5 bg-[#9E4735] text-[#FAF9F6]">À la une</span>}
        {item.paper_available === false && <span data-testid={`extract-digital-only-${item.id}`} className="text-[10px] uppercase tracking-[0.2em] font-body px-2 py-0.5 border border-[#2C402E] text-[#2C402E]">Numérique uniquement</span>}
        {!item.published && <span className="text-[10px] uppercase tracking-[0.2em] font-body px-2 py-0.5 bg-[#757575] text-[#FAF9F6]">Brouillon</span>}
      </div>
      <p className="text-sm font-body text-[#4A4A4A]">{item.author}{item.paper_available !== false && item.price_paper_label ? ` · Papier ${item.price_paper_label}` : ""}{item.price_digital_label ? ` · Numérique ${item.price_digital_label}` : ""}{!item.price_paper_label && !item.price_digital_label && item.price_label ? ` · ${item.price_label}` : ""}</p>
      <p className="text-xs font-mono text-[#757575] mt-1 truncate">/histoires/{item.slug}</p>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs font-body text-[#4A4A4A] mt-2">
        <Stat icon={Eye} value={item.views} label="lecture" plural="lectures" testid={`extract-views-${item.id}`} />
        <Stat icon={MessageCircle} value={item.whatsapp_clicks} label="clic WhatsApp" plural="clics WhatsApp" testid={`extract-wa-clicks-${item.id}`} />
        <Stat icon={MousePointerClick} value={item.secondary_clicks} label="clic secondaire" plural="clics secondaires" testid={`extract-sec-clicks-${item.id}`} />
      </div>
      <div className="flex flex-wrap gap-3 mt-3">
        <a href={`/histoires/${item.slug}`} target="_blank" rel="noreferrer" data-testid={`view-extract-${item.id}`} className="text-xs font-body text-[#2C402E] flex items-center gap-1"><ExternalLink size={12} /> Voir la page</a>
        <a href={`/histoires/${item.slug}/affiche`} target="_blank" rel="noreferrer" data-testid={`poster-extract-${item.id}`} className="text-xs font-body text-[#2C402E] flex items-center gap-1"><Printer size={12} /> Affiche QR</a>
        <button onClick={onEdit} data-testid={`edit-extract-${item.id}`} className="text-xs font-body text-[#2C402E] flex items-center gap-1"><Pencil size={12} /> Éditer</button>
        <button onClick={onDelete} data-testid={`delete-extract-${item.id}`} className="text-xs font-body text-[#9E4735] flex items-center gap-1"><Trash2 size={12} /> Supprimer</button>
      </div>
    </div>
  </li>
  );
};
